
import $ from 'jquery';
import {Utils} from '../common/Utils';
import {BaseClass} from "../base/BaseClass";

/**
 * A component is a basic UI block that will be instantiated to a specific DOM element, called component container.
 * The lifecycle of a component contains the following steps:
 *
 *  1. Instantiation - new Component({...})
 *  2. onBeforeInit  - {@link ComponentConfig#autoInit} = true or init() manually called
 *  3. init
 *  4. onInit
 *
 * During initialization time, all defined ui alias will be analyzed, events and actions are bound,
 * and data binding starts.
 *
 * See {@link Application} how to automatically initialize a custom component during application booting.
 *
 * An example for a common use-case for extending the Component class:
 * @example <caption>Creating a component</caption>
 *  // Define a new class that extends from Component
 *  class ExampleComponent extends Component {
 *
 *    constructor(config) {
 *      super({...,
 *        ui: {
 *          aliasName: '.any-selector' // accessible as jQuery object via this.ui.aliasName
 *        },
 *        events: {
 *          // attach "handleClick" handler implemented in the child instance to the DOM event 'click'
 *          // for the element behind the selector ".selector > button"
 *          'click .selector > button': 'handleClick',
 *
 *          // same as above, but using the shortcut reference to the alias "this.ui.aliasName"
 *          'click @aliasName': 'handleClick',
 *        }
 *      })
 *    }
 *
 *    handleClick(e) {
 *      // will be called after a click
 *      this.ui.aliasName.text('Hello World');
 *    }
 *  }
 *
 *  @example <caption>Define user interaction handler in the markup</caption>
 *
 *  Within the container element of the component it is possible to define user interaction events directly
 *  in the markup by using the data-action attribute. The callback handler will be automatically attached to
 *  the event (default is 'click') during initialization time.
 *
 *    <button data-action="handleClick">Click me!</button>
 *    <button data-action="handleClick" data-event="mousedown"></button>
 *
 *  @example <caption>Define UI shortcuts in the markup</caption>
 *
 *  Instead of defining UI selectors in the JavaScript class constructor, it's possible to bind an element
 *  with the data attribute "data-ui". In the following code, the element will ba accessible in the
 *  component class with "this.ui.btnClick".
 *
 *    <button data-ui="btnClick">Click me!</button>
 *
 *  @example <caption>Define data-binding in DOM elements</caption>
 *
 *    <span data-attr="foo"></span> <!-- using jQuery.text() by default to attach the value -->
 *    <span data-attr="foo" data-output="html"></span> <!-- using jQuery.html() to attach the value -->
 *
 *  It's possible to define any jQuery function name in data-output, but keep in mind the value behind the attribute
 *  will be passed always as first argument to the jQuery function.
 *
 *  Inject the attribute value by using set() function from the component class:
 *    let example = new ExampleComponent(...);
 *    example.set("foo", "Hello World!");
 *
 */
export class Component extends BaseClass {

  /**
   * Configuration object for the {@link Component} class.
   * This is a child configuration object of {@link BaseClassConfig}.
   * @typedef {BaseClassConfig} ComponentConfig
   * @property {String|jQuery} [el=null] - DOM selector or a jQuery object that is the container element
   * @property {Boolean} [autoInit=true] - auto initialization after instantiation
   * @property {Object.<String, String>} [ui] - key is an alias to access the jQuery object behind the value (jQuery selector)
   * @property {Object.<String, *>} [attr] - preset of data for the component
   * @property {Object.<String, String|Function>} [events] - events to be auto-bind to a callback handler
   */

  /**
   * Creates an instance of an UI component. Allows all config options from {@link BaseClass#config}
   * @param {ComponentConfig} config - object configuration
   */
  constructor(config = {}) {

    let args = Array.prototype.slice.call(arguments);
    if (args.length > 1) {
      args.unshift(true);
      config = $.extend.apply($, args);
    }

    super(config);

    /**
     * Reference to component's DOM container (HTML element converted into jQuery object)
     * @type {jQuery}
     */
    this.el = null;
    /**
     * key value Map where the value defines a DOM selector and the key is an alias to the jQuery objects.
     * Following keys are reserved for magic binding:
     *   $action - jQuery object that contains all elements with data-action attribute (auto action binding)
     *   $attr - jQuery object that contains all elements with data-attr attribute (one way data binding)
     * @type {Object.<String, jQuery>}
     */
    this.ui = {};
    /**
     * Contains the origin definition of config.ui
     * @type {Object.<String, String>}
     * @private
     */
    this._ui = {};
    /**
     * Events to be auto-bind to a callback handler, where the key defines the event and the value is
     * a string (name of the function implemented in the child instance or a callback function)
     * @type {Object.<String, String|Function>}
     */
    this.events = {};
    /**
     * Preset of data for the component.
     * @type {Object.<String, String>}
     * @private
     */
    this._attr = config.attr || {};
    /**
     *
     * @type {Object.<String, {el: jQuery, name: String, format: Function, output: String}>}
     * @private
     */
    this._attrBindMap = {};

    if (config.el) {
      this.el = $(config.el);
    }

    this._ui = config.ui || {};
    this.events = config.events || {};

    if (config.autoInit !== false) {
      this.init();
    }
  }

  /**
   * Initialize the component: build the UI alias map, bind events,
   * auto bind actions with a callback function from the child instance
   * and bind attributes
   *
   * @fires beforeInit
   * @fires init
   */
  init() {
    /**
     * Fire event before initialization. Tries to call {@link onBeforeInit}
     * @event beforeInit
     */
    this.triggerMethodOn('beforeInit');
    this._bindUi();
    this._bindEvents();
    this._bindActions();
    this._bindAttr();
    /**
     * Fire event after initialization. Tries to call {@link onInit}
     * @event init
     */
    this.triggerMethodOn('init');
  }

  /**
   * Format and inject the given value into the DOM element that defines
   * data-attr as the given key.
   * @param {String} key - attribute name
   * @param {*} value - attribute's value
   * @private
   */
  _setAttr(key, value) {
    if (this._attr[key] !== value) {
      this._attr[key] = value;
      let e = this._attrBindMap[key];
      if (e) {
        e.el[e.output](e.format(key, value));
      }
    }
  }

  /**
   * Inject the given value inro the DOM.
   * @param {Object<String, *>|String} key - object of key-value data or a string for a key name
   * @param {*} [value] - any type of value to be injected
   * @return {Component}
   */
  set(key, value) {
    if ($.isPlainObject(key)) {
      for (let attrKey in key) {
        if (key.hasOwnProperty(attrKey)) {
          this._setAttr(attrKey, key[attrKey]);
        }
      }
    }
    else {
      this._setAttr(key, value);
    }
    return this;
  }

  /**
   * Returns the value of the attribute with given key.
   * @param {String} key - attribute key name
   * @return {*}
   */
  get(key) {
    if (!this._attr.hasOwnProperty(key)) {
      return null;
    }
    return this._attr[key];
  }

  /**
   * Searching the given selector below component's container element (this.el)
   * or if not defined lookup document wide.
   * @param {String} sel - DOM selector
   * @return {jQuery}
   * @private
   */
  _find(sel) {
    return this.el !== null ? this.el.find(sel) : $(sel);
  }

  /**
   * Iterate through config.ui and replace the selector with a jQuery object
   * that matches the selector.
   * @private
   */
  _bindUi() {
    for (const uiKey in this._ui) {
      this.ui[uiKey] = Utils.isString(this._ui[uiKey]) ?
        this._find(this._ui[uiKey]) : this._ui[uiKey];
    }
    if (this.el === null) return;
    let that = this;
    this.el.find('[data-ui]').each(function () {
      let el = $(this);
      let key = el.data('ui');
      that.ui[key] = el;
    })
  }

  /**
   * Searching for child DOM elements with data-action definition and automatically bind
   * the UI event to the function implemented in the child subclass.
   * Event name can be defined with data-event, default: click
   * @private
   */
  _bindActions() {
    if (this.el === null) return;

    let $action = this.ui.$action = this.el.find('[data-action]');
    $action.each((i, el) => {
      let $elem = $(el);
      let eventName = $elem.data('event') || 'click';
      $elem.on(eventName, this._handleAction.bind(this));
      this._bounds.push({
        el: $elem,
        event: eventName
      });
    });
  }

  /**
   * Searching for DOM elements with data-attr definition and put it into a cache.
   * DOM will be filled by using set() function also called "one way data binding"
   * @private
   */
  _bindAttr() {
    if (this.el === null) return;

    this._attrBindMap = {};
    let $attr = this.ui.$attr = this.el.find('[data-attr]');
    $attr.each((i, el) => {
      let $elem = $(el);
      let data = $elem.data();
      let attr = {
        el: $elem,
        name: data.attr,
        format: this._formatAttr.bind(this),
        output: data.output || 'text'
      };
      if (Utils.isFunction($elem[attr.output])) {
        this._attrBindMap[attr.name] = attr;
      }
    });
  }

  /**
   * Function wrapper that using {@link invoke} to call config.methods.formatAttr (if exists)
   * or formatAttr() from the instance here.
   * @param {String} key - attribute key name
   * @param {*} value - value of the attribute
   * @return {String}
   * @private
   */
  _formatAttr(key, value) {
    return this.invoke('formatAttr', [key, value]);
  }

  /**
   * A stub method to be implemented by child class.
   * Will be used to format a data attribute's value.
   * @param {String} key - attribute key name
   * @param {*} value - value of the attribute
   * @return {String}
   */
  formatAttr(key, value) {
    return value + '';
  }

  /**
   * Re-initialize ui, events, actions and attributes
   * @see {@link _unbindEvents}
   * @see {@link _bindUi}
   * @see {@link _bindEvents}
   * @see {@link _bindActions}
   * @see {@link _bindAttr}
   * @private
   */
  _rebind() {
    this._unbindEvents();
    this._bindUi();
    this._bindEvents();
    this._bindActions();
    this._bindAttr();
  }

  /**
   * Bind all events defined with config.event during initialization.
   * @private
   */
  _bindEvents() {

    /**
     * Stores all event bindings to give the possibility for unbinding.
     * @type {Array<{el: jQuery, eventName: String}>}
     * @private
     */
    this._bounds = [];

    for (const eventKey in this.events) {

      if (!this.events.hasOwnProperty(eventKey)) {
        continue;
      }

      let handler = this.events[eventKey];

      // short syntax (only function name from current class)
      if (Utils.isString(handler)) {
        handler = this[handler];
      }
      // custom callback function
      if (Utils.isFunction(handler)) {
        handler = {
          fn: handler
        }
      }

      if (!handler.ctx) {
        handler.ctx = this;
      }
      if (typeof(handler.fn) !== 'function') {
        continue;
      }
      const split = eventKey.split(' ');
      if (split.length < 2) {
        continue;
      }
      const eventName = split.shift();
      let selector = split.join(' ');
      let $elem = this.resolveSelector(selector);
      if (!$elem) {
        continue;
      }

      $elem.on(eventName, (function(context, h, boundArgs) {
        return function () {
          let old = Array.prototype.slice.call(arguments);
          if (boundArgs) {
            old = old.concat(boundArgs);
          }
          h.apply(context, old);
        }
      }(handler.ctx, handler.fn, handler.args)));
      this._bounds.push({
        el: $elem,
        event: eventName
      });
    }
  }

  /**
   * Searching for a jQuery DOM element for the given selector.
   * Selector can be the short @-syntax for 'ui' elements,
   * a DOM selector or a jQuery element.
   * @param {String|jQuery} selector - @-syntax, DOM selector or jQuery element
   * @return {null|jQuery}
   * @private
   */
  resolveSelector(selector) {
    let $elem;
    if (!selector) {
      return null;
    }

    // check @ui syntax
    if (Utils.isString(selector) && selector.charAt(0) === '@') {
      selector = selector.substr(1);
      if (!this.ui.hasOwnProperty(selector)) {
        return null;
      }
      $elem = this.ui[selector];
    }
    else {
      $elem = this._find(selector);
    }
    return !$elem || $elem.length === 0 ? null : $elem;
  }

  /**
   * Remove all event handler that have been previously bound via
   * config.event during initialization time.
   * @private
   */
  _unbindEvents() {
    if (this._bounds) {
      this._bounds.forEach((it) => it.el.off(it.event));
      this._bounds = [];
    }
  }

  /**
   * Wrapper function that will be bound to DOM element which defines
   * the data-action.
   * @param {Event} e - origin event
   * @private
   */
  _handleAction(e) {
    e.preventDefault();
    let $btn = $(e.currentTarget);
    let action = Utils.dashToCamelCase($btn.data('action') || "");
    if (action) {
      action = 'on' + action.charAt(0).toUpperCase() + action.substr(1);
      this.invoke(action, [e, $btn]);
    }
  }

  /**
   * @name Component#onBeforeInit
   * @function
   * @memberof Component
   * @description To be called at first in {@link init}
   */

  /**
   * @name Component#onInit
   * @function
   * @memberof Component
   * @description To be called after initialization in {@link init}
   */
}
